import { inject } from 'inversify';

import { STATUS } from '@/constants';
import { UserController } from '@/controllers/UserController';
import { service } from '@/decorators/service';
import { SignalrListener, SignalrMessage } from '@/decorators/signalr';
import { Logger } from '@/services/Logger';
import { Signalr } from '@/services/signalr/Signalr';
import { PriorityStore } from '@/stores/PriorityStore';
import { orderByField } from '@/utils/orderBy';
import { IPriorityInfo } from '~/api/IPriorityInfo';
import type { IPriorityRespMsg } from '~/api/messages/IPriorityRespMsg';
import type { IPriorityUpdateRespMsg } from '~/api/messages/IPriorityUpdateRespMsg';

@SignalrListener
@service(PriorityManager)
export class PriorityManager {
  @inject(Logger) protected logger: Logger;
  @inject(Signalr) protected signalr: Signalr;
  @inject(UserController) protected userController: UserController;
  @inject(PriorityStore) protected priorityStore: PriorityStore;

  protected loaded = false;

  get hasLoaded() {
    return this.loaded;
  }

  find(id?: string) {
    return this.priorityStore.find(id);
  }

  get all() {
    const priorities = this.priorityStore.all()
      .filter((priority) => priority.status !== STATUS.DISABLED);
    return orderByField(priorities, this.priorityStore.valueKey);
  }

  reset() {
    this.loaded = false;
    this.priorityStore.set([]);
  }

  updatePriorities(priorities: IPriorityInfo[]) {
    this.loaded = true;
    this.priorityStore.batchUpdate(priorities);
  }

  @SignalrMessage('PriorityMsg')
  protected onPriorityMsg(response: IPriorityRespMsg) {
    this.updatePriorities(response.priorities);
  }

  @SignalrMessage('PriorityUpdateRespMsg')
  protected onPriorityUpdateRespMsg(response: IPriorityUpdateRespMsg) {
    this.updatePriorities([response.priority]);
  }
}
